/**
 * SMSPRO-178 — Zeitfenster- und Sichtbarkeitslogik der konfigurierbaren TopBar-Chips.
 *
 * WARUM HIER (und nicht je einmal im BFF und im Frontend): Die Entscheidung
 * „Chip sichtbar ja/nein" faellt an zwei Stellen — im BFF beim Ausliefern von
 * /topbar-chips/active und im Frontend beim Rendern (dort erneut, weil ein
 * Zeitfenster zwischen zwei Abrufen ablaufen kann). Zwei getrennte
 * Implementierungen derselben Regel weichen frueher oder spaeter voneinander ab,
 * und zwar lautlos: der Chip haengt fest oder erscheint zu frueh, ohne Fehler.
 * Deshalb genau eine Implementierung, hier, mit Tests.
 *
 * ZEIT-SEMANTIK (Vorgabe des Auftraggebers):
 *   - kein Start  -> gilt ab sofort (untere Grenze offen)
 *   - kein Ende   -> gilt unbegrenzt (obere Grenze offen)
 *   - beides leer -> dauerhaft sichtbar
 * Beide Grenzen sind EINSCHLIESSEND (now >= from, now <= to).
 */
/**
 * Wandelt eine Grenzangabe in einen Zeitstempel.
 *
 * Rueckgabe:
 *   null      = Grenze bewusst offen (null / leer / nur Leerzeichen)
 *   NaN       = Wert gesetzt, aber nicht lesbar -> Datenfehler
 *   sonst     = Millisekunden
 */
function parseBound(value) {
    if (value === null || value === undefined)
        return null;
    if (typeof value === 'string' && value.trim() === '')
        return null;
    return new Date(value).getTime();
}
/**
 * Ermittelt den Zeitfenster-Zustand eines Chips.
 *
 * Unlesbare Zeitangaben ergeben 'invalid' und damit KEINE Anzeige. Bewusst
 * fail-closed: ein Chip mit kaputtem Enddatum darf nicht unbegrenzt haengen
 * bleiben — genau der Fehler, der sonst niemandem auffaellt.
 */
export function getChipWindowState(chip, now = new Date()) {
    if (!chip.is_active)
        return 'disabled';
    const from = parseBound(chip.active_from);
    const to = parseBound(chip.active_to);
    if ((from !== null && Number.isNaN(from)) || (to !== null && Number.isNaN(to))) {
        return 'invalid';
    }
    // Start nach Ende: das Fenster kann nie eintreten. Als Konfigurationsfehler
    // melden, statt es als „laeuft noch nicht" zu verschleiern.
    if (from !== null && to !== null && from > to)
        return 'invalid';
    const ts = now.getTime();
    if (from !== null && ts < from)
        return 'pending';
    if (to !== null && ts > to)
        return 'expired';
    if (from === null && to === null)
        return 'permanent';
    return 'active';
}
/** Liegt der Chip aktuell in seinem Zeitfenster? */
export function isChipWithinWindow(chip, now = new Date()) {
    const state = getChipWindowState(chip, now);
    return state === 'active' || state === 'permanent';
}
/**
 * Ist der Chip fuer die uebergebene Rolle freigegeben?
 *
 * Leere Rollenliste = fuer alle sichtbar. Die Einschraenkung ist eine
 * Zielgruppen-Auswahl, keine Rechtepruefung: ein Super-Admin sieht einen auf
 * andere Rollen eingeschraenkten Chip bewusst NICHT, sonst bekaeme genau die
 * Gruppe die meiste Anzeige-Last, die sie am wenigsten braucht.
 */
export function isChipVisibleForRole(chip, role) {
    const roles = chip.visible_roles;
    if (!Array.isArray(roles) || roles.length === 0)
        return true;
    if (!role)
        return false;
    return roles.includes(role);
}
/**
 * Gilt der Chip fuer die anfragende Oberflaeche?
 * Ohne Angabe am Chip (null) gilt er ueberall — das ist der Normalfall und die
 * Vorgabe. Ist er gesetzt, muss die Oberflaeche uebereinstimmen.
 */
export function isChipVisibleForApp(chip, app) {
    const chipApp = chip.app;
    if (chipApp === null || chipApp === undefined || chipApp === '')
        return true;
    if (!app)
        return false;
    return String(chipApp).toUpperCase() === String(app).toUpperCase();
}
/**
 * Filtert die anzuzeigenden Chips: Hauptschalter, Zeitfenster, Rolle und
 * Oberflaeche. Der Mandant ist NICHT Teil dieser Pruefung — Chips liegen pro
 * Mandanten-DB, die Trennung passiert also bereits beim Laden.
 */
export function selectVisibleChips(chips, now = new Date(), role, app) {
    if (!Array.isArray(chips))
        return [];
    return chips.filter((chip) => isChipWithinWindow(chip, now) && isChipVisibleForRole(chip, role) && isChipVisibleForApp(chip, app));
}
// ========== Umgebungs-Chip (Nacharbeit SMSPRO-178) ==========
/**
 * Der Umgebungs-Hinweis („TEST - Alpha") war bis v0.115 fest in der Kopfzeile
 * verdrahtet und kam aus den Build-Variablen VITE_ENV_BADGE*. Damit gab es zwei
 * Wege fuer dieselbe Sache: einen verwalteten und einen unsichtbaren. In der
 * Verwaltung fehlte der Chip, obwohl er sichtbar in der Kopfzeile stand.
 *
 * Jetzt ist er ein regulaerer Eintrag. Diese Ableitung existiert nur noch fuer
 * die EINMALIGE Uebernahme im BFF: aus den vorhandenen Build-Variablen wird ein
 * Chip erzeugt, danach ist der Datensatz die Wahrheit — spaetere Aenderungen an
 * den Variablen wirken bewusst NICHT mehr, sonst wuerde eine Bearbeitung in der
 * Verwaltung beim naechsten Neustart lautlos ueberschrieben.
 */
export const ENV_BADGE_VARIANTS = {
    dev: { color: '#e53935', defaultLabel: 'DEV' },
    test: { color: '#fbc02d', defaultLabel: 'TEST - Alpha' },
    staging: { color: '#1976d2', defaultLabel: 'STAGING' },
};
/** Variante, die greift, wenn VITE_ENV_BADGE_VARIANT fehlt oder unbekannt ist. */
const ENV_BADGE_FALLBACK_VARIANT = 'test';
/**
 * Liest einen Wert aus der Umgebung. Zuerst mit VITE_-Praefix (so heissen die
 * Variablen im UI-Build), danach ohne — das BFF laeuft in einem eigenen
 * Container, dessen Umgebung die Variable auch unpraefixt gesetzt haben kann.
 */
function readEnvValue(env, name) {
    return env[`VITE_${name}`] ?? env[name];
}
/**
 * Leitet aus den Build-Variablen den zu uebernehmenden Umgebungs-Chip ab.
 *
 * Rueckgabe null bedeutet: KEIN Chip anlegen. Das ist der Normalfall auf PROD,
 * wo VITE_ENV_BADGE nicht gesetzt bzw. 'false' ist — dort soll kein Hinweis
 * entstehen, den anschliessend jemand von Hand wegraeumen muss.
 */
export function resolveEnvBadgeChip(env) {
    if (!env || typeof env !== 'object')
        return null;
    if (String(readEnvValue(env, 'ENV_BADGE') ?? '').trim().toLowerCase() !== 'true')
        return null;
    const variantKey = readEnvValue(env, 'ENV_BADGE_VARIANT')?.trim().toLowerCase() || ENV_BADGE_FALLBACK_VARIANT;
    const variant = ENV_BADGE_VARIANTS[variantKey] ?? ENV_BADGE_VARIANTS[ENV_BADGE_FALLBACK_VARIANT];
    const label = readEnvValue(env, 'ENV_BADGE_LABEL')?.trim() || variant.defaultLabel;
    // Die Spalte fasst 60 Zeichen. Kuerzen statt Anlegen scheitern lassen — ein
    // zu langer Text darf die Uebernahme nicht verhindern.
    return { label: label.slice(0, 60), color: variant.color };
}
//# sourceMappingURL=topbarChips.js.map